import type { Roundup, SummaryTableRowType } from '@repo/supergraph/roundup/base/types';
import type { MarketplaceEntityWithStates } from '../base/params/geo-targeting/types';
import { STATES_MAP } from '../marketplace/constants';
import type { StateAbbreviation } from '../marketplace/types';
import type { RoundupSummaryTableRowMetadata } from '../roundup/base/metadata/types';

export function getMarketplaceEntityStates({
  crossSellSection,
  productCardsSection,
  recapListSection,
  summaryTableSection,
}: Roundup): StateAbbreviation[] {
  const getStates = (
    items?: { marketplaceEntity: MarketplaceEntityWithStates }[],
  ): StateAbbreviation[] => items?.flatMap(item => item.marketplaceEntity.states ?? []) ?? [];

  const states = [
    ...getStates(productCardsSection.categorySections?.flatMap(s => s.productCards)),
    ...getStates(crossSellSection?.productCards),
    ...getStates(recapListSection?.recapItems),
    ...getStates(summaryTableSection.rows),
  ].filter(Boolean);

  // Unique states
  return Array.from(new Set(states));
}

export const isValidState = (state: string): state is StateAbbreviation => state in STATES_MAP;

const isLocalEntity = (entity: MarketplaceEntityWithStates) => {
  const states = entity?.states;
  return states && states?.length > 0;
};

type EntityCountByLocationType = {
  none: number;
  [key: string]: number;
};

// Taken from https://github.com/NerdWallet/query0/blob/b2d9ee792034b8d475573e676f20bb7b1cd24cc7/src/schema/wp/extensions/roundup/schema.js#L679
export const getEntityCountByLocation = (
  rows: SummaryTableRowType[] | RoundupSummaryTableRowMetadata[],
) => {
  const entityCountByLocation: EntityCountByLocationType = { none: 0 };
  const seenEntitiesMap: Record<string, boolean> = {};

  const marketplaceEntities = rows.map(row => row.marketplaceEntity);

  marketplaceEntities.forEach(entity => {
    const entityId = entity.id;

    // Check the seen entities map so we don't double count
    if (!seenEntitiesMap[entityId]) {
      seenEntitiesMap[entityId] = true;

      if (!isLocalEntity(entity)) {
        const currentCount = entityCountByLocation.none || 0;
        entityCountByLocation.none = currentCount + 1;
      } else {
        entity.states?.forEach(state => {
          const currentCount = entityCountByLocation[state] || 0;
          entityCountByLocation[state] = currentCount + 1;
        });
      }
    }
  });

  return entityCountByLocation;
};
